/**
 * LE JOURNAL ET LES ENVOIS D'UNE ALARME, EN TOUTES LETTRES.
 * ============================================================================
 *
 * INTENTION : le journal répond à « pourquoi celle-là n'est pas partie »
 * (S:7793) dans les mots du client, et les envois disent « SMS à Karim,
 * 02h03, remis » (matrice, `episode_voir`).
 *
 * Ce qui est lu, et seulement cela (liste blanche de la façade,
 * `alarmesv2.episodes.js`) :
 *  • une ligne de journal : `type`, `at`, `instant_du_fait`, `par`,
 *    `_id_user_cible`, `motif_retention`, `motif_non_envoi_canal` et `details`
 *    réduit aux clés de son type (`DETAILS_PAR_TYPE`) ;
 *  • un envoi : `_id_user`, `canal`, `rang`, `motif_envoi`, `statut`,
 *    `motif_non_envoi_canal` et les instants de son parcours.
 * Les valeurs (statuts, motifs) sont celles du service (`socle/journal.py`
 * `MOTIFS_RETENTION`, `MOTIFS_NON_ENVOI_CANAL` ; `diffusion/porte.py`,
 * `diffusion/evenements.py` pour les statuts).
 *
 * Règles : un code que l'écran ne sait pas dire ne se montre jamais tel quel
 * (« raison non disponible », « résultat non disponible ») ; un identifiant
 * de personne absent de `personnes` reste sans nom (« une personne de
 * l'équipe »), jamais un identifiant à l'écran ; un instant illisible ne
 * s'invente pas (« — »).
 */
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"
import type {
    AlarmesV2Envoi,
    AlarmesV2LigneJournal,
    AlarmesV2Nature,
    AlarmesV2Personne,
} from "@modules/alarmes-v2/types/alarmes-v2"
import { formaterInstant } from "./mesure"

/** Les douze types de ligne que la façade sert (`DETAILS_PAR_TYPE`). */
export const TYPES_DE_LIGNE = [
    "occurrence_ouverte",
    "occurrence_close",
    "prise_en_compte",
    "retour_normal",
    "envoi_demande",
    "relance_emise",
    "envoi_retenu",
    "relance_arretee",
    "livraison_remise_non_confirmee",
    "lien_ouvert",
    "etat_change",
    "avis_utilite_donne",
] as const

/** Ce qu'une ligne marque sur la courbe : les changements d'état et l'acquittement. */
export type SorteDeLigne =
    | "ouverture"
    | "close"
    | "acquittement"
    | "normale"
    | "envoi"
    | "autre"

/** Une ligne de journal prête à afficher. */
export interface LigneLue {
    /** Le numéro de la ligne, le même sur la courbe. */
    num: number
    /** L'instant du fait en millisecondes, `null` s'il est illisible. */
    ms: number | null
    /** « le 02/10 à 02h03 », ou « — ». */
    quand: string
    titre: string
    texte: string
    sorte: SorteDeLigne
}

/** Un envoi prêt à afficher. */
export interface EnvoiLu {
    ms: number | null
    quand: string
    canal: string
    personne: string
    resultat: string
    ton: "ok" | "warn" | "danger" | "neutre"
}

/** Ce qu'il faut pour dire une ligne. */
export interface ContexteJournal {
    t: Traduire
    personnes?: Record<string, AlarmesV2Personne>
    /** `relances.max` de l'alarme : « Rappel 2 sur 4 ». */
    relancesMax?: number | null
    /** La nature de l'alarme : une information s'ouvre « Information ouverte ». */
    nature?: AlarmesV2Nature | null
}

/** Un instant ISO lisible, en millisecondes, ou `null`. */
function instant(iso: unknown): number | null {
    if (typeof iso !== "string" || iso === "") return null
    const ms = new Date(iso).getTime()
    return Number.isNaN(ms) ? null : ms
}

/** Une chaîne servie, ou `null`. */
const chaine = (v: unknown): string | null =>
    typeof v === "string" && v !== "" ? v : null

/** Un entier servi, ou `null`. */
const entier = (v: unknown): number | null =>
    typeof v === "number" && Number.isInteger(v) ? v : null

/**
 * Le nom complet d'une personne servie, ou « une personne de l'équipe ».
 * Jamais l'identifiant.
 * @param id - l'identifiant servi
 * @param personnes - le bloc `personnes` de la réponse
 * @param t - la traduction
 */
export function nomDeLaPersonne(
    id: string | null | undefined,
    personnes: Record<string, AlarmesV2Personne> | undefined,
    t: Traduire
): string {
    const p = id ? personnes?.[id] : undefined
    const nom = [p?.prenom, p?.nom]
        .filter((x): x is string => typeof x === "string" && x !== "")
        .join(" ")
    return nom === "" ? t("alarmesV2.envois.personneInconnue") : nom
}

/** Le nom d'un canal servi, ou « Canal non disponible ». */
function nomDuCanal(canal: unknown, t: Traduire): string {
    const canaux = MESSAGES_ALARMES_V2.envois.canaux as Record<string, string>
    return typeof canal === "string" && canal in canaux
        ? t(`alarmesV2.envois.canaux.${canal}`)
        : t("alarmesV2.envois.canalInconnu")
}

/**
 * Pourquoi un canal n'est pas parti (`motif_non_envoi_canal`), en mots.
 * @param motif - le motif servi
 * @param canal - `sms` ou `mail` : « pas de numéro » ou « pas d'adresse mail »
 * @param t - la traduction
 */
export function raisonDeNonEnvoi(
    motif: string | null | undefined,
    canal: string | null | undefined,
    t: Traduire
): string {
    const raisons = MESSAGES_ALARMES_V2.envois.raisons as Record<string, string>
    if (!motif) return t("alarmesV2.envois.raisons.inconnue")
    const parCanal = `${motif}_${canal === "sms" ? "sms" : "mail"}`
    if (parCanal in raisons) return t(`alarmesV2.envois.raisons.${parCanal}`)
    if (motif in raisons && !motif.endsWith("_sms") && !motif.endsWith("_mail"))
        return t(`alarmesV2.envois.raisons.${motif}`)
    return t("alarmesV2.envois.raisons.inconnue")
}

/** Pourquoi un envoi a été retenu, ou pourquoi un canal n'est pas parti. */
function raisonDeLaLigne(ligne: AlarmesV2LigneJournal, t: Traduire): string {
    const retenues = MESSAGES_ALARMES_V2.journal.retenues as Record<
        string,
        string
    >
    const retention = chaine(ligne.motif_retention)
    if (retention && retention in retenues)
        return t(`alarmesV2.journal.retenues.${retention}`)
    if (ligne.motif_non_envoi_canal)
        return raisonDeNonEnvoi(
            ligne.motif_non_envoi_canal,
            chaine(ligne.details?.canal),
            t
        )
    return t("alarmesV2.envois.raisons.inconnue")
}

/** Le titre d'un envoi selon son motif : alarme, rappel, fin, test. */
function titreDEnvoi(
    motif: string | null,
    rang: number | null,
    ctx: ContexteJournal
): string {
    const { t } = ctx
    if (motif === "relance" && rang !== null) {
        const max = entier(ctx.relancesMax)
        return max && max > 0
            ? t("alarmesV2.journal.titres.rappelSur", { n: rang, max })
            : t("alarmesV2.journal.titres.rappel", { n: rang })
    }
    if (motif === "retour_normale" || motif === "sonde_revenue")
        return t("alarmesV2.journal.titres.envoiFin")
    if (motif === "test") return t("alarmesV2.journal.titres.envoiTest")
    return t("alarmesV2.journal.titres.envoi")
}

/** L'état d'une surveillance après un changement, en mots. */
function phraseDEtat(
    details: AlarmesV2LigneJournal["details"],
    t: Traduire
): string {
    const vers = chaine(details?.vers)
    const motifsEtat = MESSAGES_ALARMES_V2.motifsEtat as Record<string, string>
    const motifsDesact = MESSAGES_ALARMES_V2.motifsDesactivation as Record<
        string,
        string
    >
    const etats: Record<string, string> = {
        armee: t("alarmesV2.etats.enService"),
        non_surveillee: t("alarmesV2.etats.nePeutPasPrevenir"),
        desactivee: t("alarmesV2.etats.desactivee"),
    }
    if (!vers || !(vers in etats)) return ""
    const motifEtat = chaine(details?.etat_motif)
    const motifDesact = chaine(details?.desactivation_motif)
    const motif =
        vers === "desactivee" && motifDesact && motifDesact in motifsDesact
            ? t(`alarmesV2.motifsDesactivation.${motifDesact}`)
            : vers === "non_surveillee" && motifEtat && motifEtat in motifsEtat
              ? t(`alarmesV2.motifsEtat.${motifEtat}`)
              : ""
    return motif
        ? t("alarmesV2.journal.textes.etatVersMotif", {
              etat: etats[vers],
              motif,
          })
        : t("alarmesV2.journal.textes.etatVers", { etat: etats[vers] })
}

/** Le titre, le texte et la sorte d'une ligne. */
function direLaLigne(
    ligne: AlarmesV2LigneJournal,
    ctx: ContexteJournal
): Pick<LigneLue, "titre" | "texte" | "sorte"> {
    const { t, personnes } = ctx
    const d = ligne.details ?? {}
    const cible = nomDeLaPersonne(ligne._id_user_cible, personnes, t)
    const auteur = nomDeLaPersonne(ligne.par, personnes, t)
    const canal = nomDuCanal(d.canal, t)
    switch (ligne.type) {
        case "occurrence_ouverte":
            return {
                sorte: "ouverture",
                titre:
                    ctx.nature === "pour_information"
                        ? t("alarmesV2.journal.titres.ouvertureInformation")
                        : t("alarmesV2.journal.titres.ouverture"),
                texte:
                    d.motif_ouverture === "silence_sonde"
                        ? t("alarmesV2.journal.textes.ouvertureDefautMesure")
                        : t("alarmesV2.journal.textes.ouvertureDepassement"),
            }
        case "occurrence_close":
            return {
                sorte: "close",
                titre: t("alarmesV2.journal.titres.close"),
                texte: ligne.par
                    ? t("alarmesV2.journal.textes.closeVue", { nom: auteur })
                    : d.close_motif === "retour_normal"
                      ? t("alarmesV2.journal.textes.closeNormale")
                      : t("alarmesV2.journal.textes.close"),
            }
        case "prise_en_compte":
            return {
                sorte: "acquittement",
                titre: t("alarmesV2.journal.titres.acquittement"),
                texte:
                    d.canal === "ecran"
                        ? t("alarmesV2.journal.textes.acquitteeEcran", {
                              nom: auteur,
                          })
                        : d.canal === "lien"
                          ? t("alarmesV2.journal.textes.acquitteeLien", {
                                nom: auteur,
                            })
                          : t("alarmesV2.journal.textes.acquittee", {
                                nom: auteur,
                            }),
            }
        case "retour_normal":
            return {
                sorte: "normale",
                titre: t("alarmesV2.journal.titres.normale"),
                texte: t("alarmesV2.journal.textes.normale"),
            }
        case "envoi_demande":
        case "relance_emise":
            return {
                sorte: "envoi",
                titre: titreDEnvoi(chaine(d.motif_envoi), entier(d.rang), ctx),
                texte: t("alarmesV2.journal.textes.envoiVers", {
                    canal,
                    nom: cible,
                }),
            }
        case "envoi_retenu": {
            const raison = raisonDeLaLigne(ligne, t)
            return {
                sorte: "envoi",
                titre: t("alarmesV2.journal.titres.retenu"),
                texte: ligne.motif_non_envoi_canal
                    ? t("alarmesV2.journal.textes.nonEnvoyeVers", {
                          canal,
                          nom: cible,
                          raison,
                      })
                    : t("alarmesV2.journal.textes.nonEnvoye", { raison }),
            }
        }
        case "relance_arretee": {
            const arrets = MESSAGES_ALARMES_V2.journal.arrets as Record<
                string,
                string
            >
            const motif = chaine(d.arret_motif)
            const nb = entier(d.nb)
            const texte = [
                motif && motif in arrets
                    ? t(`alarmesV2.journal.arrets.${motif}`)
                    : "",
                nb !== null && nb > 0
                    ? t("alarmesV2.journal.textes.arretNb", { n: nb })
                    : "",
            ]
                .filter((x) => x !== "")
                .join(" ")
            return {
                sorte: "autre",
                titre: t("alarmesV2.journal.titres.arret"),
                texte,
            }
        }
        case "livraison_remise_non_confirmee":
            return {
                sorte: "envoi",
                titre: t("alarmesV2.journal.titres.remiseNonConfirmee"),
                texte: t("alarmesV2.journal.textes.remiseNonConfirmee", {
                    canal,
                    nom: cible,
                }),
            }
        case "lien_ouvert":
            return {
                sorte: "autre",
                titre: t("alarmesV2.journal.titres.lienOuvert"),
                texte: ligne.par
                    ? t("alarmesV2.journal.textes.lienOuvertPar", {
                          nom: auteur,
                      })
                    : "",
            }
        case "etat_change":
            return {
                sorte: "autre",
                titre: t("alarmesV2.journal.titres.etat"),
                texte: phraseDEtat(d, t),
            }
        case "avis_utilite_donne":
            return {
                sorte: "autre",
                titre: t("alarmesV2.journal.titres.avis"),
                texte:
                    d.valeur === "utile"
                        ? t("alarmesV2.journal.textes.avisUtile")
                        : d.valeur === "pas_utile"
                          ? t("alarmesV2.journal.textes.avisPasUtile")
                          : "",
            }
        default:
            // Un type que l'écran ne sait pas dire (la façade en ajoute un) :
            // la ligne se montre « non disponible », jamais tue ni en code.
            return {
                sorte: "autre",
                titre: t("alarmesV2.journal.titres.inconnu"),
                texte: "",
            }
    }
}

/** Trie par instant, les instants illisibles en dernier, l'ordre servi gardé sinon. */
function parInstant<T extends { ms: number | null }>(a: T, b: T): number {
    if (a.ms === null && b.ms === null) return 0
    if (a.ms === null) return 1
    if (b.ms === null) return -1
    return a.ms - b.ms
}

/**
 * Les lignes du journal, rangées dans l'ordre du temps et numérotées. Une
 * ligne d'un type que l'écran ne sait pas dire se montre « non disponible » :
 * aucune ligne servie n'est tue.
 * @param lignes - `journal` de la réponse
 * @param ctx - la traduction, les noms, le contexte de l'alarme
 */
export function lireLeJournal(
    lignes: AlarmesV2LigneJournal[] | null | undefined,
    ctx: ContexteJournal
): LigneLue[] {
    return (Array.isArray(lignes) ? lignes : [])
        .map((ligne) => {
            const iso = chaine(ligne?.instant_du_fait) ?? chaine(ligne?.at)
            return {
                ligne,
                iso,
                ms: instant(iso),
                ...direLaLigne(ligne ?? {}, ctx),
            }
        })
        .sort(parInstant)
        .map((l, i) => ({
            num: i + 1,
            ms: l.ms,
            quand:
                (l.ms !== null ? formaterInstant(l.iso, ctx.t) : null) ??
                ctx.t("alarmesV2.mesure.absente"),
            titre: l.titre,
            texte: l.texte,
            sorte: l.sorte,
        }))
}

/** Le résultat d'un envoi, en mots, et son ton. */
function resultat(
    envoi: AlarmesV2Envoi,
    t: Traduire
): { texte: string; ton: EnvoiLu["ton"] } {
    const quand = (iso: string | null | undefined) => formaterInstant(iso, t)
    switch (envoi.statut) {
        case "remis": {
            const q = quand(envoi.remis_le)
            return {
                texte: q
                    ? t("alarmesV2.envois.resultats.remis", { quand: q })
                    : t("alarmesV2.envois.resultats.remisSansHeure"),
                ton: "ok",
            }
        }
        case "ouvert": {
            const q = quand(envoi.ouvert_le)
            return {
                texte: q
                    ? t("alarmesV2.envois.resultats.ouvert", { quand: q })
                    : t("alarmesV2.envois.resultats.remisSansHeure"),
                ton: "ok",
            }
        }
        case "accepte":
            return envoi.remise_non_confirmee_le
                ? {
                      texte: t("alarmesV2.envois.resultats.remiseNonConfirmee"),
                      ton: "warn",
                  }
                : {
                      texte: t("alarmesV2.envois.resultats.accepte"),
                      ton: "neutre",
                  }
        case "a_envoyer":
        case "demande":
            return {
                texte: t("alarmesV2.envois.resultats.enCours"),
                ton: "neutre",
            }
        case "echoue":
        case "mort":
            return {
                texte: t("alarmesV2.envois.resultats.nonRemis"),
                ton: "danger",
            }
        case "rejete":
            return {
                texte: t("alarmesV2.envois.resultats.nonEnvoye", {
                    raison: raisonDeNonEnvoi(
                        envoi.motif_non_envoi_canal,
                        envoi.canal,
                        t
                    ),
                }),
                ton: "warn",
            }
        default:
            return {
                texte: t("alarmesV2.envois.resultats.inconnu"),
                ton: "neutre",
            }
    }
}

/** « rappel 2 sur 4, », « message de fin, » ou rien. */
function prefixeDEnvoi(envoi: AlarmesV2Envoi, ctx: ContexteJournal): string {
    const { t } = ctx
    const rang = entier(envoi.rang)
    if (envoi.motif_envoi === "relance" && rang !== null) {
        const max = entier(ctx.relancesMax)
        return max && max > 0
            ? t("alarmesV2.envois.prefixeRappelSur", { n: rang, max })
            : t("alarmesV2.envois.prefixeRappel", { n: rang })
    }
    if (
        envoi.motif_envoi === "retour_normale" ||
        envoi.motif_envoi === "sonde_revenue"
    )
        return t("alarmesV2.envois.prefixeFin")
    if (envoi.motif_envoi === "test") return t("alarmesV2.envois.prefixeTest")
    return ""
}

/**
 * Les envois, dans l'ordre du temps (premier instant servi de chacun).
 * @param envois - `livraisons` de la réponse
 * @param ctx - la traduction, les noms, le contexte de l'alarme
 */
export function lireLesEnvois(
    envois: AlarmesV2Envoi[] | null | undefined,
    ctx: ContexteJournal
): EnvoiLu[] {
    const { t } = ctx
    return (Array.isArray(envois) ? envois : [])
        .map((e) => {
            const iso =
                [e.ecrite_le, e.demande_le, e.rejete_le, e.accepte_le]
                    .map(chaine)
                    .find((x) => x !== null) ?? null
            const r = resultat(e, t)
            return {
                ms: instant(iso),
                quand: formaterInstant(iso, t) ?? t("alarmesV2.mesure.absente"),
                canal: nomDuCanal(e.canal, t),
                personne: nomDeLaPersonne(e._id_user, ctx.personnes, t),
                resultat: prefixeDEnvoi(e, ctx) + r.texte,
                ton: r.ton,
            }
        })
        .sort(parInstant)
}
